import React, { useMemo } from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';

const PASSWORD_REQUIREMENTS = [
  { regex: /.{8,}/, label: 'At least 8 characters' },
  { regex: /[A-Z]/, label: 'At least one uppercase letter (A-Z)' },
  { regex: /[a-z]/, label: 'At least one lowercase letter (a-z)' },
  { regex: /[0-9]/, label: 'At least one number (0-9)' },
  { regex: /[!@#$%^&*(),.?":{}|<>\-_=+[\]\\;'/~]/, label: 'At least one special character (!@#$%...)' },
];

const getStrengthColor = (percent) => {
  if (percent <= 20) return '#E74C3C'; // Red - Weak
  if (percent <= 40) return '#E67E22'; // Orange - Fair
  if (percent <= 60) return '#F39C12'; // Yellow-Orange - Moderate
  if (percent <= 80) return '#F1C40F'; // Yellow - Good
  return '#27AE60'; // Green - Strong
};

const getStrengthLabel = (percent) => {
  if (percent <= 20) return 'Weak';
  if (percent <= 40) return 'Fair';
  if (percent <= 60) return 'Moderate';
  if (percent <= 80) return 'Good';
  return 'Strong';
};

/**
 * Password Strength Indicator Component
 * Displays a progress bar and checklist showing password requirements
 *
 * @param {Object} props
 * @param {string} props.password - The password to evaluate
 * @param {boolean} props.showRequirements - Whether to show the requirements checklist (default: true)
 * @param {boolean} props.darkMode - Whether to use dark mode styling (default: false)
 */
const PasswordStrengthIndicator = ({ password = '', showRequirements = true, darkMode = false }) => {
  const { requirements, strengthPercent } = useMemo(() => {
    const reqs = PASSWORD_REQUIREMENTS.map(({ regex, label }) => ({
      label,
      met: regex.test(password),
    }));

    const metCount = reqs.filter((r) => r.met).length;
    const percent = (metCount / PASSWORD_REQUIREMENTS.length) * 100;

    return { requirements: reqs, strengthPercent: percent };
  }, [password]);

  const strengthColor = getStrengthColor(strengthPercent);
  const strengthLabel = getStrengthLabel(strengthPercent);

  // Theme colors
  const textColor = darkMode ? 'rgba(255, 255, 255, 0.7)' : '#666666';
  const mutedColor = darkMode ? 'rgba(255, 255, 255, 0.5)' : '#999999';
  const disabledColor = darkMode ? 'rgba(255, 255, 255, 0.3)' : '#BDBDBD';
  const bgColor = darkMode ? 'rgba(255, 255, 255, 0.1)' : '#E5E5E5';

  // Don't show anything if password is empty
  if (!password) {
    return (
      <Box sx={{ mt: 1.5, mb: 1 }}>
        <Typography
          variant="caption"
          sx={{ color: textColor, fontFamily: 'Metropolis, sans-serif' }}
        >
          Password must contain:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.5 }}>
          {PASSWORD_REQUIREMENTS.map((req, index) => (
            <Box
              key={index}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
            >
              <CancelIcon sx={{ fontSize: 16, color: disabledColor }} />
              <Typography
                variant="caption"
                sx={{
                  color: mutedColor,
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.75rem',
                }}
              >
                {req.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1.5, mb: 1 }}>
      {/* Progress Bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <LinearProgress
          variant="determinate"
          value={strengthPercent}
          sx={{
            flex: 1,
            mr: 1.5,
            height: 6,
            borderRadius: 3,
            backgroundColor: bgColor,
            '& .MuiLinearProgress-bar': {
              backgroundColor: strengthColor,
              borderRadius: 3,
              transition: 'transform 0.3s ease, background-color 0.3s ease',
            },
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: strengthColor,
            fontWeight: 600,
            fontFamily: 'Metropolis, sans-serif',
            minWidth: 60,
          }}
        >
          {strengthLabel}
        </Typography>
      </Box>

      {/* Requirements Checklist */}
      {showRequirements && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {requirements.map((req, index) => (
            <Box
              key={index}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
            >
              {req.met ? (
                <CheckCircleIcon sx={{ fontSize: 16, color: '#27AE60' }} />
              ) : (
                <CancelIcon sx={{ fontSize: 16, color: '#E74C3C' }} />
              )}
              <Typography
                variant="caption"
                sx={{
                  color: req.met ? '#27AE60' : textColor,
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.75rem',
                  transition: 'color 0.2s ease',
                }}
              >
                {req.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PasswordStrengthIndicator;
